import { useState, useEffect, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useFinance } from '@/contexts/FinanceContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Repeat, CalendarDays, CalendarRange } from 'lucide-react';
import type { FinanceTransaction, RecurrenceActionScope } from '@/types/finance';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: FinanceTransaction | null;
  actionType: 'edit' | 'delete';
  onConfirm: (scope: RecurrenceActionScope) => void;
}

export function RecurrenceActionDialog({
  open,
  onOpenChange,
  transaction,
  actionType,
  onConfirm,
}: Props) {
  const [scope, setScope] = useState<RecurrenceActionScope>('single');
  const { transactions } = useFinance();

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open) setScope('single');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const groupInfo = useMemo(() => {
    if (!transaction?.recurrenceGroupId) return null;
    const group = transactions.filter((t) => t.recurrenceGroupId === transaction.recurrenceGroupId);
    const future = group.filter((t) => t.dueDate >= transaction.dueDate);
    return {
      total: group.length,
      future: future.length,
      transactionDate: format(transaction.dueDate, 'dd/MM/yyyy', { locale: ptBR }),
    };
  }, [transaction, transactions]);

  if (!transaction || !groupInfo) return null;

  const title =
    actionType === 'delete' ? 'Excluir transação recorrente' : 'Editar transação recorrente';
  const actionLabel = actionType === 'delete' ? 'Excluir' : 'Continuar';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-primary" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>
            "{transaction.description}" faz parte de uma série de {groupInfo.total} transações.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <p className="mb-3 text-sm font-medium">Aplicar em:</p>
          <RadioGroup value={scope} onValueChange={(v) => setScope(v as RecurrenceActionScope)}>
            {(
              [
                {
                  value: 'single' as const,
                  icon: CalendarDays,
                  title: 'Apenas esta transação',
                  desc: `Somente ${groupInfo.transactionDate}`,
                },
                {
                  value: 'future' as const,
                  icon: CalendarRange,
                  title: 'Esta e todas as futuras',
                  desc: `${groupInfo.future} transação(ões) a partir de ${groupInfo.transactionDate}`,
                },
                {
                  value: 'all' as const,
                  icon: Repeat,
                  title: 'Toda a série',
                  desc: `Todas as ${groupInfo.total} transações`,
                },
              ] as const
            ).map(({ value, icon: Icon, title: t, desc }) => (
              <div
                key={value}
                className="mt-2 flex cursor-pointer items-start space-x-3 rounded-lg border border-border p-3 first:mt-0 hover:bg-accent/50"
                onClick={() => setScope(value)}
              >
                <RadioGroupItem value={value} id={value} className="mt-0.5" />
                <div>
                  <Label
                    htmlFor={value}
                    className="flex cursor-pointer items-center gap-2 font-medium"
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {t}
                  </Label>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              onConfirm(scope);
              onOpenChange(false);
            }}
            className={
              actionType === 'delete'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : ''
            }
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
